import { NodeModel } from "@minoru/react-dnd-treeview";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { FileProperties } from "../models/FileProperties";

export interface ShelfSearchResult {
	id: number;
	name: string;
	path: string;
	isDroppable: boolean;
	hasChildren: boolean;
	node: NodeModel<FileProperties>;
}

interface UseShelfSearchProps {
	shelfNodes: NodeModel<FileProperties>[];
}

export const useShelfSearch = ({ shelfNodes }: UseShelfSearchProps) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [searchResults, setSearchResults] = useState<ShelfSearchResult[]>([]);
	const [showSearchResults, setShowSearchResults] = useState(false);
	const [focusedSearchIndex, setFocusedSearchIndex] = useState(-1);
	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const searchResultsRefs = useRef<(HTMLElement | null)[]>([]);

	const searchIndex = useMemo((): ShelfSearchResult[] => {
		const index: ShelfSearchResult[] = [];

		const nodeMap = new Map<number, NodeModel<FileProperties>>();
		shelfNodes.forEach((node) => {
			if (node.id !== undefined && node.id !== null) {
				nodeMap.set(node.id as number, node);
			}
		});

		const pathCache = new Map<number, string[]>();

		const buildNodePath = (nodeId: number): string[] => {
			if (pathCache.has(nodeId)) return pathCache.get(nodeId)!;
			const node = nodeMap.get(nodeId);
			if (!node) return [];

			const parentPath =
				node.parent === -1 ? [] : buildNodePath(node.parent as number);
			const path = [...parentPath, node.text];
			pathCache.set(nodeId, path);
			return path;
		};

		const parentIds = new Set(shelfNodes.map((n) => n.parent));

		shelfNodes.forEach((node) => {
			if (node.id === undefined || node.id === null || !node.text) return;

			const nodePath = buildNodePath(node.id as number);
			const pathStr = nodePath.join(" > ");

			index.push({
				id: node.id as number,
				name: node.text,
				path: pathStr,
				isDroppable: node.droppable || false,
				hasChildren: parentIds.has(node.id as number),
				node: node,
			});
		});

		return index;
	}, [shelfNodes]);

	const searchNodes = useCallback(
		(term: string): ShelfSearchResult[] => {
			const lowerTerm = term.toLowerCase();
			return searchIndex.filter((item) =>
				item.name.toLowerCase().includes(lowerTerm),
			);
		},
		[searchIndex],
	);

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const term = event.target.value;
		setSearchTerm(term);

		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
		}

		if (term.trim() === "" || term.length < 2) {
			setSearchResults([]);
			setShowSearchResults(false);
			setFocusedSearchIndex(-1);
			return;
		}

		searchTimeoutRef.current = setTimeout(() => {
			const results = searchNodes(term);
			setSearchResults(results);
			setShowSearchResults(results.length > 0);
			const newFocusIndex = results.length > 0 ? 0 : -1;
			setFocusedSearchIndex(newFocusIndex);
			searchResultsRefs.current = new Array(results.length).fill(null);
		}, 250);
	};

	const handleKeyDown = (
		event: React.KeyboardEvent<HTMLDivElement>,
	): ShelfSearchResult | undefined => {
		if (event.key === "Escape") {
			if (showSearchResults) {
				setSearchTerm("");
				setSearchResults([]);
				setShowSearchResults(false);
				setFocusedSearchIndex(-1);
			}
			return undefined;
		}

		if (showSearchResults && searchResults.length > 0) {
			if (event.key === "ArrowDown") {
				event.preventDefault();
				const newIndex =
					focusedSearchIndex < searchResults.length - 1
						? focusedSearchIndex + 1
						: 0;
				setFocusedSearchIndex(newIndex);
				setTimeout(() => scrollFocusedItemIntoView(newIndex), 0);
			} else if (event.key === "ArrowUp") {
				event.preventDefault();
				const newIndex =
					focusedSearchIndex > 0
						? focusedSearchIndex - 1
						: searchResults.length - 1;
				setFocusedSearchIndex(newIndex);
				setTimeout(() => scrollFocusedItemIntoView(newIndex), 0);
			} else if (event.key === "Enter" && focusedSearchIndex >= 0) {
				event.preventDefault();
				const selectedResult = searchResults[focusedSearchIndex];
				return selectedResult;
			}
		}

		return undefined;
	};

	const scrollFocusedItemIntoView = (index: number) => {
		if (searchResultsRefs.current[index]) {
			searchResultsRefs.current[index]?.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	};

	const clearSearch = () => {
		setSearchTerm("");
		setSearchResults([]);
		setShowSearchResults(false);
		setFocusedSearchIndex(-1);
	};

	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}
		};
	}, []);

	return {
		searchTerm,
		searchResults,
		showSearchResults,
		focusedSearchIndex,
		searchResultsRefs,
		handleSearchChange,
		handleKeyDown,
		clearSearch,
	};
};
